import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import type { PageResponse } from "@/lib/pagination";
import { buildListQuery } from "@/lib/pagination";
import type { ProductItem, ProductCategory } from "@/lib/types";
import Pagination from "@/components/base/Pagination";
import ScrollReveal from "@/components/base/ScrollReveal";
import { useServerPagination } from "@/lib/useServerPagination";
import ImageUploadField from "@/components/feature/ImageUploadField";
import PdfUploadField from "@/components/feature/PdfUploadField";
type FormMode = "list" | "create" | "edit";

const emptyForm = {
  name: "",
  categoryId: "" as string | number,
  status: "active" as string,
  description: "",
  pdfUrl: "",
  info: "",
  mediaUrl: "",
};

export default function AdminProducts() {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [mode, setMode] = useState<FormMode>("list");
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const fetchCategories = useCallback(async () => {
    try {
      const catRes = await api.get<ProductCategory[]>("/admin/products/categories");
      if (catRes.data) setCategories(catRes.data);
    } catch (err: unknown) {
      showToast("카테고리 로딩 실패: " + (err instanceof Error ? err.message : "오류"));
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const list = useServerPagination<ProductItem>({
    resetDeps: [search, filterCategory],
    enabled: mode === "list",
    fetchPage: useCallback(
      async (page, size) => {
        const res = await api.getPage<PageResponse<ProductItem>>(
          "/admin/products" +
            buildListQuery(page + 1, size, {
              search: search || undefined,
              categoryId: filterCategory || undefined,
            })
        );
        return res.data;
      },
      [search, filterCategory]
    ),
  });

  const handleDelete = async (id: number) => {
    if (!confirm("정말 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      showToast("삭제되었습니다.");
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "삭제 실패");
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { showToast("제품명을 입력해주세요."); return; }
    const media = form.mediaUrl.trim() ? [{ url: form.mediaUrl.trim(), type: "image" }] : [];
    const payload = {
      name: form.name.trim(),
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      status: form.status,
      description: form.description.trim() || null,
      pdfUrl: form.pdfUrl.trim() || null,
      info: form.info.trim() || null,
      media,
    };
    try {
      if (mode === "edit" && editId) {
        await api.put(`/admin/products/${editId}`, payload);
        showToast("수정되었습니다.");
      } else {
        await api.post("/admin/products", payload);
        showToast("생성되었습니다.");
      }
      setMode("list");
      setForm({ ...emptyForm });
      setEditId(null);
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "저장 실패");
    }
  };

  const openEdit = (item: ProductItem) => {
    setEditId(item.id);
    setForm({
      name: item.name,
      categoryId: item.categoryId ?? "",
      status: item.status,
      description: item.description ?? "",
      pdfUrl: item.pdfUrl ?? "",
      info: item.info ?? "",
      mediaUrl: item.media && item.media.length > 0 ? item.media[0].url : "",
    });
    setMode("edit");
  };

  const categoryName = (cid: number | null) => {
    if (!cid) return "—";
    return categories.find((c) => c.id === cid)?.name ?? "—";
  };

  return (
    <>
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 bg-[#2c4964] text-white text-sm rounded-full shadow-lg">
          {toast}
        </div>
      )}

      <div>
          {mode === "list" && (
            <>
              <ScrollReveal>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="search" placeholder="Search products..." value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20 w-full sm:w-64"
                      />
                    </div>
                    <select
                      value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white"
                    >
                      <option value="">All Categories</option>
                      {categories.map((c) => (
                        <option key={c.id} value={String(c.id)}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => { setMode("create"); setForm({ ...emptyForm }); setEditId(null); }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" /> Add Product
                  </button>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={0.05}>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#f1f7fc]">
                        <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-16">ID</th>
                        <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Name</th>
                        <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Category</th>
                        <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Status</th>
                        <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-40">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.loading ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
                      ) : list.items.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No products found.</td></tr>
                      ) : (
                        list.items.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 border-b border-gray-100 text-gray-500">{item.id}</td>
                            <td className="px-4 py-3 border-b border-gray-100 text-gray-700 font-medium">{item.name}</td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {categoryName(item.categoryId)}
                              </span>
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                item.status === "active" ? "bg-green-50 text-green-700"
                                : item.status === "inactive" ? "bg-red-50 text-red-700"
                                : "bg-yellow-50 text-yellow-700"
                              }`}>{item.status}</span>
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              <div className="flex items-center gap-2">
                                <button onClick={() => openEdit(item)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[#1977cc] hover:bg-[#f1f7fc] rounded transition-colors cursor-pointer">
                                  <i className="ri-edit-line" /> Edit
                                </button>
                                <button onClick={() => handleDelete(item.id)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer">
                                  <i className="ri-delete-bin-line" /> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {!list.loading && (
                    <Pagination
                      page={list.page}
                      totalPages={list.totalPages}
                      totalItems={list.totalItems}
                      pageSize={list.pageSize}
                      rangeStart={list.rangeStart}
                      rangeEnd={list.rangeEnd}
                      onPageChange={list.setPage}
                      onPageSizeChange={list.setPageSize}
                      pageSizeOptions={list.pageSizeOptions}
                    />
                  )}
                </div>
              </ScrollReveal>
            </>
          )}

          {(mode === "create" || mode === "edit") && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <div className="max-w-2xl mx-auto bg-white rounded-xl border border-gray-200 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-[#2c4964]">
                    {mode === "create" ? "Add Product" : "Edit Product"}
                  </h2>
                  <button onClick={() => { setMode("list"); setForm({ ...emptyForm }); setEditId(null); }}
                    className="text-sm text-gray-500 hover:text-[#1977cc] transition-colors cursor-pointer">Cancel</button>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Name <span className="text-red-500">*</span></label>
                    <input type="text" value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20"
                      placeholder="Product name" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Category</label>
                      <select value={String(form.categoryId)}
                        onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white">
                        <option value="">— Select —</option>
                        {categories.map((c) => (
                          <option key={c.id} value={String(c.id)}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Status</label>
                      <select value={form.status}
                        onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white">
                        <option value="active">active</option>
                        <option value="inactive">inactive</option>
                        <option value="draft">draft</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Description</label>
                    <textarea value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      rows={3} maxLength={500}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] resize-none"
                      placeholder="Short description" />
                    <p className="mt-1 text-xs text-gray-400 text-right">{form.description.length}/500</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <ImageUploadField label="Product Image" value={form.mediaUrl}
                      onChange={(url) => setForm((f) => ({ ...f, mediaUrl: url }))} />
                    <PdfUploadField label="Product PDF" value={form.pdfUrl}
                      onChange={(url) => setForm((f) => ({ ...f, pdfUrl: url }))} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Info / Extra text</label>
                    <input type="text" value={form.info}
                      onChange={(e) => setForm((f) => ({ ...f, info: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc]"
                      placeholder="Short info label" />
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button onClick={() => { setMode("list"); setForm({ ...emptyForm }); setEditId(null); }}
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors cursor-pointer">Cancel</button>
                    <button onClick={handleSave}
                      className="px-5 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap">
                      {mode === "create" ? "Create Product" : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
      </div>
    </>
  );
}
